<script lang="ts">
  // ============================================================
  // ✅ Svelte 5 Runes Component
  // ============================================================
  // 사용자 프로필 페이지

  import { router } from '@inertiajs/svelte'

  interface User {
    id: number
    name: string
    nickname?: string
    email: string
    username?: string
    avatar_url?: string
    avatar_attached?: boolean
    bio?: string
    organization_name?: string
    department?: string
    team?: string
    role: string
    verified: boolean
    work_email?: string
    company_domain?: string
    company_verified?: boolean
    provider?: string
    created_at: string
    public_profile_url?: string
    stats?: {
      posts_count: number
      comments_count: number
      likes_count: number
      followers_count: number
    }
  }

  let { user = {} as User, flash = {} }: { user: User; flash: Record<string, string> } = $props()

  let isEditing = $state(false)
  let isSaving = $state(false)
  let avatarFile: File | null = $state(null)
  // svelte-ignore state_referenced_locally -- intentional: preview initialized from prop
  let avatarPreview = $state(user.avatar_url || '')
  let uploadMethod = $state<'url' | 'file'>('url')
  // svelte-ignore state_referenced_locally -- intentional: form fields initialized from prop
  let formData = $state({
    username: user.username || '',
    nickname: user.nickname || '',
    bio: user.bio || '',
    avatar_url: user.avatar_url || ''
  })

  function toggleEdit() {
    if (isEditing) {
      formData = {
        username: user.username || '',
        nickname: user.nickname || '',
        bio: user.bio || '',
        avatar_url: user.avatar_url || ''
      }
      avatarFile = null
      avatarPreview = user.avatar_url || ''
      uploadMethod = 'url'
    }
    isEditing = !isEditing
  }

  function handleFileChange(e: Event) {
    const target = e.target as HTMLInputElement
    const file = target.files?.[0]

    if (file) {
      avatarFile = file
      const reader = new FileReader()
      reader.onload = (e) => {
        avatarPreview = e.target?.result as string
      }
      reader.readAsDataURL(file)
    }
  }

  function handleSubmit(e: Event) {
    e.preventDefault()
    isSaving = true

    const data: any = {
      username: formData.username,
      nickname: formData.nickname,
      bio: formData.bio
    }

    // 업로드 방식에 따라 데이터 추가
    if (uploadMethod === 'file' && avatarFile) {
      data.avatar = avatarFile
    } else if (uploadMethod === 'url') {
      data.avatar_url = formData.avatar_url
    }

    router.patch('/profile', {
      user: data
    }, {
      forceFormData: uploadMethod === 'file', // 파일 업로드 시 FormData 사용
      onFinish: () => {
        isSaving = false
        isEditing = false
      }
    })
  }

  function formatDate(dateString: string) {
    return new Date(dateString).toLocaleDateString('ko-KR', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    })
  }
</script>

<div class="space-y-6">
  <!-- Header -->
  <header class="bg-surface border-b border-default">
    <div class="py-6">
      <h1 class="text-h1 text-content">내 프로필</h1>
    </div>
  </header>

  <main class="py-8 max-w-2xl mx-auto">
    {#if flash.notice}
      <div class="mb-6 p-4 bg-success-light dark:bg-success/20 border border-success/30 rounded-xl text-success-dark dark:text-success text-body2">
        {flash.notice}
      </div>
    {/if}

    <!-- Profile Card -->
    <div class="card p-8 mb-6">
      <div class="flex items-start gap-6 mb-8">
        <!-- Avatar -->
        <div class="flex-shrink-0">
          <div class="w-24 h-24 rounded-2xl overflow-hidden bg-gradient-to-br from-primary to-blue-600 flex items-center justify-center">
            {#if user.avatar_url}
              <img src={user.avatar_url} alt={user.name} class="w-full h-full object-cover" />
            {:else}
              <span class="text-4xl font-bold text-white">
                {user.nickname?.charAt(0) || user.name?.charAt(0) || '?'}
              </span>
            {/if}
          </div>
        </div>

        <!-- Profile Info -->
        <div class="flex-1">
          <div class="flex items-start justify-between mb-4">
            <div>
              <h2 class="text-h2 text-content mb-1">{user.nickname || user.name}</h2>
              <p class="text-body2 text-content-secondary">{user.email}</p>
              {#if user.username}
                <a href={user.public_profile_url} target="_blank" rel="noreferrer" class="inline-flex mt-2 text-caption text-primary hover:underline">
                  공개 프로필 보기: @{user.username}
                </a>
              {/if}
            </div>
            <button
              onclick={toggleEdit}
              class="btn btn-primary btn-sm"
            >
              {isEditing ? '취소' : '수정'}
            </button>
          </div>

          {#if !isEditing}
            <div class="space-y-3">
              {#if user.organization_name}
                <div class="flex items-center gap-2">
                  <span class="text-caption font-medium text-content-tertiary w-20">조직:</span>
                  <span class="text-body2 text-content">{user.organization_name}</span>
                </div>
              {/if}

              {#if user.department}
                <div class="flex items-center gap-2">
                  <span class="text-caption font-medium text-content-tertiary w-20">부서:</span>
                  <span class="text-body2 text-content">{user.department}</span>
                </div>
              {/if}

              {#if user.team}
                <div class="flex items-center gap-2">
                  <span class="text-caption font-medium text-content-tertiary w-20">팀:</span>
                  <span class="text-body2 text-content">{user.team}</span>
                </div>
              {/if}

              {#if user.bio}
                <div class="flex items-start gap-2">
                  <span class="text-caption font-medium text-content-tertiary w-20">소개:</span>
                  <span class="text-body2 text-content">{user.bio}</span>
                </div>
              {/if}

              <div class="flex items-center gap-2">
                <span class="text-caption font-medium text-content-tertiary w-20">가입일:</span>
                <span class="text-body2 text-content">{formatDate(user.created_at)}</span>
              </div>
            </div>
          {/if}
        </div>
      </div>

      {#if user.verified}
        <div class="flex items-center gap-2 mb-6">
          <svg class="w-5 h-5 text-success" fill="currentColor" viewBox="0 0 20 20">
            <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clip-rule="evenodd" />
          </svg>
          <span class="text-body2 font-medium text-success">이메일 인증 완료</span>
        </div>
      {/if}

      <!-- Edit Form -->
      {#if isEditing}
        <form onsubmit={handleSubmit} class="space-y-6 border-t border-default pt-6">
          <!-- Nickname -->
          <div>
            <label for="profile-username" class="block text-label font-medium text-gray-700 dark:text-dark-text-medium mb-2">공개 username</label>
            <input
              id="profile-username"
              type="text"
              bind:value={formData.username}
              placeholder="예: seunghan"
              class="input w-full"
              maxlength="30"
            />
            <p class="text-caption text-content-tertiary mt-1">
              영문 소문자, 숫자, 언더스코어만 사용 가능합니다.
            </p>
          </div>

          <!-- Nickname -->
          <div>
            <label for="profile-nickname" class="block text-label font-medium text-gray-700 dark:text-dark-text-medium mb-2">닉네임</label>
            <input
              id="profile-nickname"
              type="text"
              bind:value={formData.nickname}
              placeholder="닉네임 입력"
              class="input w-full"
              maxlength="50"
            />
            <p class="text-caption text-content-tertiary mt-1">
              {formData.nickname.length}/50
            </p>
          </div>

          <!-- Bio -->
          <div>
            <label for="profile-bio" class="block text-label font-medium text-gray-700 dark:text-dark-text-medium mb-2">소개</label>
            <textarea
              id="profile-bio"
              bind:value={formData.bio}
              placeholder="자신을 소개해주세요"
              rows="4"
              class="input w-full resize-none"
              maxlength="500"
            ></textarea>
            <p class="text-caption text-content-tertiary mt-1">
              {formData.bio.length}/500
            </p>
          </div>

          <!-- Avatar Upload -->
          <div>
            <p class="block text-label font-medium text-gray-700 dark:text-dark-text-medium mb-3">프로필 사진</p>

            <!-- 업로드 방식 선택 -->
            <div class="flex gap-4 mb-4">
              <label class="flex items-center cursor-pointer">
                <input
                  type="radio"
                  value="url"
                  bind:group={uploadMethod}
                  class="mr-2"
                />
                <span class="text-body2 text-gray-700 dark:text-dark-text-medium">URL 입력</span>
              </label>
              <label class="flex items-center cursor-pointer">
                <input
                  type="radio"
                  value="file"
                  bind:group={uploadMethod}
                  class="mr-2"
                />
                <span class="text-body2 text-gray-700 dark:text-dark-text-medium">파일 업로드</span>
              </label>
            </div>

            {#if uploadMethod === 'url'}
              <!-- URL 입력 -->
              <input
                type="url"
                bind:value={formData.avatar_url}
                placeholder="https://example.com/image.jpg"
                class="input w-full"
              />
              <p class="text-caption text-content-tertiary mt-1">외부 이미지 URL을 입력하세요</p>
            {:else}
              <!-- 파일 업로드 -->
              <div class="space-y-3">
                <input
                  type="file"
                  accept="image/*"
                  onchange={handleFileChange}
                  class="block w-full text-body2 text-content-secondary
                         file:mr-4 file:py-2 file:px-4
                         file:rounded-xl file:border-0
                         file:text-body2 file:font-medium
                         file:bg-primary/10 file:text-primary
                         hover:file:bg-primary/20
                         cursor-pointer"
                />
                <p class="text-caption text-content-tertiary">
                  JPG, PNG, GIF 파일 (최대 5MB)
                </p>

                {#if avatarPreview}
                  <div class="mt-3">
                    <p class="text-caption text-content-secondary mb-2">미리보기:</p>
                    <img
                      src={avatarPreview}
                      alt="Avatar preview"
                      class="w-32 h-32 rounded-xl object-cover border-2 border-default"
                    />
                  </div>
                {/if}
              </div>
            {/if}
          </div>

          <!-- Buttons -->
          <div class="flex justify-end gap-3 pt-4">
            <button
              type="button"
              onclick={toggleEdit}
              class="btn btn-secondary btn-md"
              disabled={isSaving}
            >
              취소
            </button>
            <button
              type="submit"
              class="btn btn-primary btn-md disabled:opacity-50"
              disabled={isSaving}
            >
              {isSaving ? '저장 중...' : '저장'}
            </button>
          </div>
        </form>
      {/if}
    </div>

    <!-- 소속 인증 섹션 -->
    <div class="card p-8 mb-6">
      <div class="flex items-center justify-between mb-6">
        <h3 class="text-h3 text-content">소속 인증</h3>
        {#if user.verified}
          <span class="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-success/10 text-success text-caption font-medium">
            <svg class="w-3.5 h-3.5" fill="currentColor" viewBox="0 0 20 20">
              <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clip-rule="evenodd" />
            </svg>
            인증 완료
          </span>
        {:else}
          <span class="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-warning/10 text-warning-dark dark:text-warning text-caption font-medium">
            <svg class="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"/>
            </svg>
            미인증
          </span>
        {/if}
      </div>

      {#if user.verified}
        <!-- 인증 완료 상태 -->
        <div class="space-y-4">
          <!-- 로그인 계정 -->
          <div class="flex items-center justify-between py-3 border-b border-gray-100 dark:border-dark-border/60">
            <div class="flex items-center gap-2">
              <svg class="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"/>
              </svg>
              <span class="text-body2 text-content-secondary">로그인 계정</span>
            </div>
            <div class="flex items-center gap-2">
              {#if user.provider === 'google'}
                <svg class="w-4 h-4" viewBox="0 0 24 24">
                  <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4"/>
                  <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
                  <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
                  <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
                </svg>
                <span class="text-body2 text-gray-700 dark:text-dark-text-medium">Google 계정</span>
              {:else if user.provider === 'apple'}
                <svg class="w-4 h-4 text-gray-800 dark:text-white" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.8-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z"/>
                </svg>
                <span class="text-body2 text-gray-700 dark:text-dark-text-medium">Apple 계정</span>
              {:else}
                <svg class="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"/>
                </svg>
                <span class="text-body2 text-gray-700 dark:text-dark-text-medium">이메일 계정</span>
              {/if}
            </div>
          </div>

          <!-- 인증된 회사 이메일 -->
          {#if user.work_email}
            <div class="flex items-center justify-between py-3 border-b border-gray-100 dark:border-dark-border/60">
              <div class="flex items-center gap-2">
                <svg class="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M21 13.255A23.931 23.931 0 0112 15c-3.183 0-6.22-.62-9-1.745M16 6V4a2 2 0 00-2-2h-4a2 2 0 00-2 2v2m4 6h.01M5 20h14a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"/>
                </svg>
                <span class="text-body2 text-content-secondary">회사 이메일</span>
              </div>
              <span class="text-body2 text-content font-medium">{user.work_email}</span>
            </div>
          {/if}

          <!-- 소속 조직 -->
          {#if user.organization_name}
            <div class="flex items-center justify-between py-3">
              <div class="flex items-center gap-2">
                <svg class="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4"/>
                </svg>
                <span class="text-body2 text-content-secondary">소속 조직</span>
              </div>
              <span class="text-body2 text-content font-medium">{user.organization_name}</span>
            </div>
          {/if}

          <!-- 재인증 안내 -->
          <div class="mt-2 p-3 rounded-xl bg-surface-tertiary">
            <p class="text-caption text-content-tertiary">
              회사 이메일을 변경하려면
              <a href="/verification" class="text-primary font-medium hover:underline">소속 인증 페이지</a>
              에서 재인증할 수 있습니다.
            </p>
          </div>
        </div>

      {:else}
        <!-- 미인증 상태 -->
        <div class="space-y-5">
          <!-- 안내 문구 -->
          <div class="flex gap-4 p-4 rounded-xl bg-primary/5 dark:bg-primary/10 border border-primary/20">
            <div class="flex-shrink-0 w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center">
              <svg class="w-5 h-5 text-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"/>
              </svg>
            </div>
            <div>
              <p class="text-body2 font-medium text-content mb-1">회사 이름을 표시하려면</p>
              <p class="text-caption text-content-tertiary leading-relaxed">
                {#if user.provider === 'google' || user.provider === 'apple'}
                  {user.provider === 'google' ? 'Google' : 'Apple'} 계정으로 로그인했어도,
                  회사 이름을 프로필에 달려면 회사 이메일로 소속을 인증해야 합니다.
                {:else}
                  프로필과 글에 회사 이름을 달려면 회사 이메일 인증이 필요합니다.
                  인증 전용 게시판은 열람이 제한될 수 있습니다.
                {/if}
              </p>
            </div>
          </div>

          <!-- 로그인 방식 표시 -->
          {#if user.provider}
            <div class="flex items-center justify-between py-3 border-b border-gray-100 dark:border-dark-border/60">
              <span class="text-body2 text-content-secondary">현재 로그인 방식</span>
              <div class="flex items-center gap-2">
                {#if user.provider === 'google'}
                  <svg class="w-4 h-4" viewBox="0 0 24 24">
                    <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4"/>
                    <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
                    <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
                    <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
                  </svg>
                  <span class="text-body2 text-gray-700 dark:text-dark-text-medium">Google 계정 ({user.email})</span>
                {:else}
                  <svg class="w-4 h-4 text-gray-800 dark:text-white" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.8-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z"/>
                  </svg>
                  <span class="text-body2 text-gray-700 dark:text-dark-text-medium">Apple 계정 ({user.email})</span>
                {/if}
              </div>
            </div>
          {/if}

          <!-- 인증 단계 안내 -->
          <div class="space-y-3">
            <p class="text-caption font-medium text-content-tertiary uppercase tracking-wide">인증 방법</p>
            <div class="space-y-2">
              {#each [
                { step: '1', text: '회사 이메일 주소 입력 (예: yourname@company.com)' },
                { step: '2', text: '해당 이메일로 발송된 인증 링크 클릭' },
                { step: '3', text: '인증 완료 후 커뮤니티 전체 기능 이용 가능' }
              ] as item}
                <div class="flex items-start gap-3">
                  <span class="flex-shrink-0 w-5 h-5 rounded-full bg-primary/10 text-primary text-caption font-bold flex items-center justify-center mt-0.5">{item.step}</span>
                  <span class="text-body2 text-content-secondary">{item.text}</span>
                </div>
              {/each}
            </div>
          </div>

          <!-- 인증하기 버튼 -->
          <a
            href="/verification"
            class="flex items-center justify-center gap-2 w-full px-6 py-3.5 bg-primary hover:bg-primary-dark text-white font-semibold rounded-xl transition-all duration-200 shadow-sm hover:shadow-md"
          >
            <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z"/>
            </svg>
            회사 계정 인증하기
          </a>
        </div>
      {/if}
    </div>

    <!-- Stats Section -->
    {#if user.stats}
      <div class="card p-8 mb-6">
        <h3 class="text-h3 text-content mb-6">활동 통계</h3>
        <div class="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div class="text-center">
            <p class="text-display text-primary font-bold">{user.stats.posts_count || 0}</p>
            <p class="text-caption text-content-tertiary mt-1">게시글</p>
          </div>
          <div class="text-center">
            <p class="text-display text-primary font-bold">{user.stats.comments_count || 0}</p>
            <p class="text-caption text-content-tertiary mt-1">댓글</p>
          </div>
          <div class="text-center">
            <p class="text-display text-primary font-bold">{user.stats.likes_count || 0}</p>
            <p class="text-caption text-content-tertiary mt-1">좋아요</p>
          </div>
          <div class="text-center">
            <p class="text-display text-primary font-bold">{user.stats.followers_count || 0}</p>
            <p class="text-caption text-content-tertiary mt-1">팔로워</p>
          </div>
        </div>
      </div>
    {/if}

    <!-- Additional Info -->
    <div class="card p-8">
      <h3 class="text-h3 text-content mb-6">계정 정보</h3>
      <div class="space-y-4">
        <div class="flex items-center justify-between pb-4 border-b border-default">
          <span class="text-body2 font-medium text-content-secondary">이메일</span>
          <span class="text-body2 text-content">{user.email}</span>
        </div>
        <div class="flex items-center justify-between pb-4 border-b border-default">
          <span class="text-body2 font-medium text-content-secondary">역할</span>
          <span class="badge">{user.role === 'super_admin' ? '관리자' : user.role === 'moderator' ? '모더레이터' : '사용자'}</span>
        </div>
        <div class="flex items-center justify-between">
          <span class="text-body2 font-medium text-content-secondary">가입일</span>
          <span class="text-body2 text-content">{formatDate(user.created_at)}</span>
        </div>
      </div>
    </div>
  </main>
</div>
